import { Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { timeAgo } from "@/components/bidding/BidHistory";
import { EmptyState } from "@/components/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/intl";
import { AccountLayout } from "@/pages/account/AccountLayout";

/** Human-readable notification text (shared with the Activity Center). */
export const notificationText = (n, t, locale) => {
  const p = n.payload || {};
  const amount = p.amount_cents ? formatCurrency(p.amount_cents, p.currency || "EUR", locale) : "";
  const map = {
    outbid: p.reason === "proxy_defense"
      ? t("ntf.outbidProxy", { name: p.horse_name || "", amount })
      : t("ntf.outbid", { name: p.horse_name || "", amount }),
    won: t("ntf.won", { name: p.horse_name || "", amount }),
    lost: t("ntf.lost", { name: p.horse_name || "" }),
    auction_live: t("ntf.auction_live"),
    registration_request: t("ntf.registration_request", { name: p.user_name || "" }),
    registration_approved: t("ntf.registration_approved", { paddle: p.paddle_number || "" }),
    registration_declined: t("ntf.registration_declined"),
    lot_closed: t("ntf.lot_closed", { name: p.horse_name || "" }),
    submission_received: t("ntf.submission_received", { name: p.horse_name || "" }),
    submission_accepted: t("ntf.submission_accepted", { name: p.horse_name || "" }),
    submission_declined: t("ntf.submission_declined", { name: p.horse_name || "" }),
    invoice_issued: t("ntf.invoice_issued", { number: p.invoice_number || "", name: p.horse_name || "" }),
    operation_payment_confirmed: t("ntf.operation_payment_confirmed", { name: p.horse_name || "" }),
    operation_docs_prepared: t("ntf.operation_docs_prepared", { name: p.horse_name || "" }),
    operation_transport_arranged: t("ntf.operation_transport_arranged", { name: p.horse_name || "" }),
    operation_delivered: t("ntf.operation_delivered", { name: p.horse_name || "" }),
    operation_completed: t("ntf.operation_completed", { name: p.horse_name || "" }),
    billing_missing: t("ntf.billing_missing", { number: p.invoice_number || "" }),
  };
  return map[n.type] || n.type;
};

export default function AccountNotifications() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => {
      setItems(data.items);
      if (data.unread > 0) api.post("/notifications/read-all").catch(() => {});
    }).catch(() => setItems([]));
  }, []);

  return (
    <AccountLayout>
      {items === null ? (
        <div className="flex flex-col gap-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Bell} title={t("acct.notificationsEmptyTitle")} description={t("acct.notificationsEmptyDesc")} data-testid="notifications-empty" />
      ) : (
        <ol className="flex flex-col divide-y divide-black/5" data-testid="account-notifications">
          {items.map((n) => (
            <li key={n.id} className="py-4 flex items-start justify-between gap-4">
              <p className="text-sm text-[#0A0A0A] leading-snug">{notificationText(n, t, locale)}</p>
              <span className="text-xs text-[#737373] shrink-0 tabular-nums">{timeAgo(n.created_at, t)}</span>
            </li>
          ))}
        </ol>
      )}
    </AccountLayout>
  );
}
